/**
 * Hook for session recording using rrweb with circular buffer
 * Captures user interactions and DOM mutations for replay
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { record } from 'rrweb';
import pako from 'pako';

// rrweb event type
interface RRWebEvent {
  type: number;
  timestamp: number;
  data: unknown;
}

// Sampling options for rrweb
interface SamplingOptions {
  mousemove?: boolean | number;
  mouseInteraction?: boolean;
  scroll?: number;
  media?: number;
  input?: 'last' | 'all';
}

export interface SessionRecordingOptions {
  /** Enable session recording (default: true) */
  enabled: boolean;
  /** Duration to keep in buffer in seconds (default: 30) */
  bufferDuration?: number;
  /** Maximum buffer size in bytes (default: 10MB) */
  maxBufferSize?: number;
  /** Sampling options for rrweb */
  sampling?: SamplingOptions;
  /** Block certain elements from recording */
  blockSelector?: string;
  /** Mask input values */
  maskInputSelector?: string;
  /** Mask all text content */
  maskTextSelector?: string;
}

export interface SessionRecording {
  /** Compressed recording data (gzip) */
  data: Uint8Array;
  /** Recording duration in milliseconds */
  duration: number;
  /** Number of events captured */
  eventCount: number;
  /** Start timestamp */
  startTime: string;
  /** End timestamp */
  endTime: string;
  /** Uncompressed size in bytes */
  originalSize: number;
  /** Compressed size in bytes */
  compressedSize: number;
}

export interface UseSessionRecordingResult {
  /** Whether recording is active */
  isRecording: boolean;
  /** Current buffer size in bytes */
  bufferSize: number;
  /** Number of events in buffer */
  eventCount: number;
  /** Export recording from buffer */
  exportRecording: () => SessionRecording | null;
  /** Clear the buffer */
  clearBuffer: () => void;
  /** Start/resume recording */
  startRecording: () => void;
  /** Pause recording */
  pauseRecording: () => void;
}

const DEFAULT_OPTIONS: Required<SessionRecordingOptions> = {
  enabled: true,
  bufferDuration: 30, // 30 seconds
  maxBufferSize: 10 * 1024 * 1024, // 10MB
  sampling: {
    mousemove: true,
    mouseInteraction: true,
    scroll: 150, // throttle scroll events
    media: 800,
    input: 'last', // only record last input value
  },
  blockSelector: '.psc-widget-dialog, [data-psc-block], .aea-chat-window, [data-rrweb-ignore]',
  maskInputSelector: 'input[type="password"], [data-psc-mask]',
  maskTextSelector: '[data-psc-mask-text]',
};

/**
 * Safe selector matcher that guards against non-Element nodes.
 * Fixes rrweb alpha bug where .matches() is called on Text/Comment nodes.
 */
function createSafeMatcher(selector: string): (node: Node | null) => boolean {
  return (node: Node | null): boolean => {
    // Guard against null/undefined
    if (!node) return false;
    // Only Element nodes have the matches() method
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const element = node as Element;
    try {
      return element.matches(selector);
    } catch {
      return false;
    }
  };
}

export function useSessionRecording(
  options: SessionRecordingOptions
): UseSessionRecordingResult {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const eventsRef = useRef<RRWebEvent[]>([]);
  const stopFnRef = useRef<(() => void) | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [bufferSize, setBufferSize] = useState(0);
  const [eventCount, setEventCount] = useState(0);

  // Calculate approximate size of an event
  const getEventSize = useCallback((event: RRWebEvent): number => {
    return JSON.stringify(event).length;
  }, []);

  // Trim events older than buffer duration
  const trimOldEvents = useCallback(() => {
    const now = Date.now();
    const cutoff = now - (config.bufferDuration * 1000);

    eventsRef.current = eventsRef.current.filter(
      (event) => event.timestamp >= cutoff
    );
  }, [config.bufferDuration]);

  // Trim events if buffer exceeds max size
  const trimBySize = useCallback(() => {
    let totalSize = 0;
    const events = eventsRef.current;

    // Calculate total size from newest to oldest
    for (let i = events.length - 1; i >= 0; i--) {
      totalSize += getEventSize(events[i]);
      if (totalSize > config.maxBufferSize) {
        // Remove older events
        eventsRef.current = events.slice(i + 1);
        break;
      }
    }
  }, [config.maxBufferSize, getEventSize]);

  // Handle new recorded event
  const handleEvent = useCallback((event: RRWebEvent) => {
    eventsRef.current.push(event);

    // Periodically trim
    if (eventsRef.current.length % 100 === 0) {
      trimOldEvents();
      trimBySize();
    }

    // Update state (throttled)
    if (eventsRef.current.length % 10 === 0) {
      setEventCount(eventsRef.current.length);
      const size = eventsRef.current.reduce(
        (acc, e) => acc + getEventSize(e),
        0
      );
      setBufferSize(size);
    }
  }, [trimOldEvents, trimBySize, getEventSize]);

  // Start recording
  const startRecording = useCallback(() => {
    if (!config.enabled || stopFnRef.current) return;

    try {
      // Create safe matcher for block selector
      // This fixes rrweb alpha bug where .matches() is called on Text/Comment nodes
      const blockMatcher = createSafeMatcher(config.blockSelector);

      // rrweb record options - use type assertion for flexibility
      const recordOptions = {
        emit: handleEvent as (event: unknown) => void,
        sampling: config.sampling,
        // Use function-based check instead of blockSelector to avoid .matches() on non-Elements
        checkBlockFn: blockMatcher,
        maskInputOptions: {
          password: true,
        },
        maskAllInputs: false,
        slimDOMOptions: {
          script: true,
          comment: true,
          headFavicon: true,
          headWhitespace: true,
          headMetaSocial: true,
          headMetaRobots: true,
          headMetaHttpEquiv: true,
          headMetaVerification: true,
          headMetaAuthorship: true,
        },
        inlineStylesheet: true,
        recordCanvas: false,
        collectFonts: true,
        // Additional safety: add error handler for uncaught rrweb errors
        errorHandler: (error: Error) => {
          // Suppress "matches is not a function" errors from rrweb internals
          if (error.message?.includes('matches is not a function')) {
            return; // Silently ignore this known rrweb alpha bug
          }
          console.error('[PSC] rrweb error:', error);
        },
      };

      stopFnRef.current = record(recordOptions) ?? null;

      setIsRecording(true);
    } catch (error) {
      console.error('[PSC] Failed to start session recording:', error);
    }
  }, [config, handleEvent]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (stopFnRef.current) {
      stopFnRef.current();
      stopFnRef.current = null;
      setIsRecording(false);
    }
  }, []);

  // Clear buffer
  const clearBuffer = useCallback(() => {
    eventsRef.current = [];
    setEventCount(0);
    setBufferSize(0);
  }, []);

  // Export recording
  const exportRecording = useCallback((): SessionRecording | null => {
    const events = eventsRef.current;

    if (events.length === 0) {
      return null;
    }

    // Trim old events before export
    trimOldEvents();

    const finalEvents = eventsRef.current;
    if (finalEvents.length === 0) {
      return null;
    }

    const jsonData = JSON.stringify(finalEvents);
    const originalSize = jsonData.length;

    // Compress with pako (gzip)
    const compressed = pako.gzip(jsonData);

    const startTime = new Date(finalEvents[0].timestamp).toISOString();
    const endTime = new Date(finalEvents[finalEvents.length - 1].timestamp).toISOString();
    const duration = finalEvents[finalEvents.length - 1].timestamp - finalEvents[0].timestamp;

    return {
      data: compressed,
      duration,
      eventCount: finalEvents.length,
      startTime,
      endTime,
      originalSize,
      compressedSize: compressed.length,
    };
  }, [trimOldEvents]);

  // Start recording on mount if enabled
  useEffect(() => {
    if (config.enabled) {
      startRecording();
    }

    return () => {
      pauseRecording();
    };
  }, [config.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic cleanup
  useEffect(() => {
    if (!config.enabled) return;

    const interval = setInterval(() => {
      trimOldEvents();
      setEventCount(eventsRef.current.length);
    }, 5000); // Every 5 seconds

    return () => clearInterval(interval);
  }, [config.enabled, trimOldEvents]);

  return {
    isRecording,
    bufferSize,
    eventCount,
    exportRecording,
    clearBuffer,
    startRecording,
    pauseRecording,
  };
}
